import React, { useState, useRef } from 'react';
import localImageService from '../services/localImageService';
import { Upload, Image as ImageIcon, Loader2 } from 'lucide-react';
import { Button } from './ui/button';

const ImageUploader = ({ onImageUploaded, multiple = false }) => {
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileSelect = async (files) => {
    if (!files || files.length === 0) return;

    const fileArray = Array.from(files);

    // Validar tipos de arquivo
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    const invalidFiles = fileArray.filter(file => !validTypes.includes(file.type));

    if (invalidFiles.length > 0) {
      alert('Apenas imagens são permitidas (JPG, PNG, GIF, WebP)');
      return;
    }

    // Validar tamanho (10MB por arquivo)
    const maxSize = 10 * 1024 * 1024; // 10MB
    const oversizedFiles = fileArray.filter(file => file.size > maxSize);

    if (oversizedFiles.length > 0) {
      alert('Cada imagem deve ter no máximo 10MB');
      return;
    }

    try {
      setUploading(true);

      const filesToUpload = multiple ? fileArray : [fileArray[0]];

      // Processa cada arquivo localmente
      const results = [];
      for (const file of filesToUpload) {
        const result = await localImageService.storeImage(file);
        if (result.success) {
          results.push(result.data.images[0]);
        } else {
          throw new Error(result.message);
        }
      }

      // Retorna os resultados
      onImageUploaded(multiple ? results : results[0]);

    } catch (error) {
      console.error('Erro no processamento:', error);
      alert('Erro ao processar as imagens: ' + error.message);
    } finally {
      setUploading(false);
      // Limpar input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);

    const files = e.dataTransfer.files;
    handleFileSelect(files);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e) => {
    handleFileSelect(e.target.files);
  };

  return (
    <div className="w-full">
      <div
        className={`
          relative border-2 border-dashed rounded-lg p-10 text-center cursor-pointer transition-all duration-300
          ${dragOver
            ? 'border-church-green bg-church-green/10 scale-[1.02]'
            : 'border-white/10 bg-white/5 hover:border-church-green/50 hover:bg-white/10'
          }
          ${uploading ? 'opacity-75 pointer-events-none' : ''}
        `}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={handleClick}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple={multiple}
          accept="image/*"
          onChange={handleFileChange}
          className="hidden"
        />

        <div className="flex flex-col items-center justify-center space-y-4">
          {uploading ? (
            <>
              <div className="p-4 rounded-full bg-church-green/20 text-church-green animate-pulse">
                <Loader2 className="h-8 w-8 animate-spin" />
              </div>
              <p className="text-gray-300 font-medium">Processando imagem...</p>
            </>
          ) : (
            <>
              <div className={`p-4 rounded-full transition-colors duration-300 ${dragOver ? 'bg-church-green text-white' : 'bg-white/10 text-gray-400 group-hover:bg-white/20'}`}>
                <ImageIcon className="h-8 w-8" />
              </div>
              <div className="space-y-1">
                <p className="text-white font-medium text-lg">
                  {multiple ? 'Clique ou arraste imagens aqui' : 'Clique ou arraste uma imagem aqui'}
                </p>
                <p className="text-sm text-gray-500">
                  JPG, PNG, GIF, WebP - Máximo 10MB {multiple ? 'por imagem' : ''}
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="mt-4 border-white/10 text-gray-300 hover:text-white hover:bg-white/10 pointer-events-none"
              >
                <Upload className="h-4 w-4 mr-2" />
                Selecionar Arquivo
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ImageUploader;